// Client store (STORE01-04, AUTH05): one per tab (module singleton via getStore()). Owns the scoped repository, the
// confirmed server state, the idempotent outbox, the metadata cache, recent searches and the cross-tab channel.
import type { StateOperation, StatePatchResponse, UserState } from '@tuned/contracts';
import { api, ApiRequestError } from '../app/api.ts';
import {
  approxBytes, budgetFor, dbName, deleteDb, isQuotaError, memoryRepo, openIdbRepo, SCHEMA_VERSION, storageBudget, tunedDbNames,
  type CacheKind, type CacheRecord, type OutboxEntry, type Repo, type Scope,
} from './db.ts';
import { applyOp } from './reducer.ts';
import { clearLocalIndex } from './localSearch.ts';

/** sessionUpdatedAt: when the session data behind this scope was fetched (react-query dataUpdatedAt). */
export type StoreScope = Scope & { sharedMode: boolean; connectionIds: string[]; sessionUpdatedAt?: number };
export type StorageStatus = { mode: 'indexeddb' | 'memory'; persisted: boolean; localSavingUnavailable: boolean; reason: string | null };
export type MutationHandle = { operationId: string; undo(): boolean };
export type CachePutOptions = { kind: CacheKind; maxAgeMs: number; sourceRevision?: string | null };
export type FlushOptions = { force?: boolean; keepalive?: boolean };
export type Snapshot = {
  state: UserState | null; loading: boolean; error: ApiRequestError | null; stale: boolean; pendingCount: number;
  storage: StorageStatus; recentSearches: string[]; scopeKey: string | null;
};

const BATCH = 50;
const MAX_RECENTS = 10;
const TOUCH_MS = 60_000;
const SHARED = 'shared-device-mode';
const MEANINGFUL = new Set<StateOperation['op']>(['list.add', 'reminder.add', 'preferences.set', 'watched.set']);
const SENSITIVE = /(^|[/:])(media|connections?|playback|session)([/:]|$)/i;
const IDLE: StorageStatus = { mode: 'memory', persisted: false, localSavingUnavailable: false, reason: null };
const memoryStatus = (reason: string): StorageStatus => ({ mode: 'memory', persisted: false, localSavingUnavailable: reason !== SHARED, reason });

/** Exponential backoff with ±20% jitter: 1 s, 2 s, 4 s … capped at 60 s. */
export function backoffDelay(failures: number, random: () => number = Math.random): number {
  return Math.round(Math.min(60_000, 1000 * 2 ** Math.max(0, failures - 1) * (0.8 + 0.4 * random())));
}

export function toApiError(e: unknown): ApiRequestError {
  if (e instanceof ApiRequestError) return e;
  return new ApiRequestError(0, { code: 'INTERNAL', message: 'Tuned could not reach the server. Check your connection and try again.', retryable: true, requestId: '' });
}

/** Credential, media and playback responses are never cached. Throws in development so the mistake is caught early. */
export function cacheable(key: string): boolean {
  if (!SENSITIVE.test(key.split('?')[0])) return true;
  if (import.meta.env.DEV) throw new Error(`Tuned store refuses to cache "${key}": credential, session, media and playback data are never stored.`);
  return false;
}

async function isPersisted(): Promise<boolean> {
  try { return (await globalThis.navigator?.storage?.persisted?.()) ?? false; } catch { return false; }
}
const openFailure = (e: unknown) => (e instanceof Error && e.message.startsWith('indexeddb-') ? e.message : 'indexeddb-open-failed');

export type Store = ReturnType<typeof createStore>;

export function createStore() {
  let scope: StoreScope | null = null;
  let key: string | null = null;
  let loggedOutAt = 0;
  let gen = 0;
  let repo: Repo = memoryRepo();
  let status = IDLE;
  let ready: Promise<void> = Promise.resolve();
  let writes: Promise<void> = Promise.resolve();
  let sharedSync: Promise<void> = Promise.resolve();
  let budget = budgetFor(null);
  let serverState: UserState | null = null;
  let outbox: OutboxEntry[] = [];
  const inFlight = new Set<string>();
  let loading = true;
  let stale = false;
  let unauthorized = false;
  let persistAsked = false;
  let error: ApiRequestError | null = null;
  let recents: string[] = [];
  let failures = 0;
  let retryAt = 0;
  let lastTs = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let running: Promise<void> = Promise.resolve();
  let queued: { p: Promise<void>; force: boolean; keepalive: boolean } | null = null;
  const known = new Set<string>();
  const listeners = new Set<() => void>();
  const bus = new Map<string, Set<(payload: unknown) => void>>();
  const logoutCbs = new Set<() => void>();
  const invalidateCbs = new Set<(keys: string[]) => void>();

  const build = (): Snapshot => ({
    state: serverState && outbox.reduce((s, e) => applyOp(s, e.operation, new Date(e.createdAt).toISOString()), serverState),
    loading, error, stale, pendingCount: outbox.length, storage: status, recentSearches: recents, scopeKey: key,
  });
  let snap = build();
  function emit() {
    snap = build();
    listeners.forEach((l) => l());
  }

  // ---- cross-tab channel (STORE04) ----
  const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('tuned');
  if (channel) channel.onmessage = (ev: MessageEvent) => onMessage(ev.data);
  function post(type: string, payload?: unknown) {
    try { channel?.postMessage({ type, payload }); } catch { /* uncloneable payload or closed channel */ }
  }
  function onMessage(m: { type?: unknown; payload?: unknown } | null) {
    if (!m || typeof m.type !== 'string') return;
    if (m.type === 'state') {
      const p = m.payload as { key: string; state: UserState; done: string[] };
      if (key && p?.key === key) {
        const done = new Set(p.done);
        outbox = outbox.filter((e) => inFlight.has(e.operationId) || !done.has(e.operationId));
        if (adopt(p.state)) stale = false;
        emit();
      }
    } else if (m.type === 'invalidate') {
      const keys = Array.isArray(m.payload) ? (m.payload as string[]) : [];
      if (repo.mode === 'memory') for (const k of keys) void write((r) => r.cacheDelete(k));
      invalidateCbs.forEach((cb) => cb(keys));
    } else if (m.type === 'logout') {
      resetLocal();
      logoutCbs.forEach((cb) => cb());
    }
    bus.get(m.type)?.forEach((cb) => cb(m.payload));
  }

  // ---- flush triggers ----
  const onOnline = () => void flush({ force: true });
  const onHide = () => void flush({ force: true, keepalive: true });
  const onVisibility = () => { if (document.visibilityState === 'hidden') onHide(); };
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVisibility);
  }

  // ---- persistence: one serialized write queue; quota errors evict, retry once, then degrade to memory (STORE01) ----
  function write(fn: (r: Repo) => Promise<unknown>): Promise<void> {
    const r = repo;
    writes = writes.then(() => guarded(r, fn)).catch((e: unknown) => {
      if (r === repo) console.warn('[store] local write failed', e); // writes to a repo closed by sign-out are moot
    });
    return writes;
  }
  async function guarded(r: Repo, fn: (r: Repo) => Promise<unknown>) {
    try {
      await fn(r);
      return;
    } catch (e) {
      if (!isQuotaError(e)) throw e;
    }
    try {
      await r.evict(Math.floor((await r.cacheBytes()) / 2), Date.now());
      await fn(r);
    } catch (e) {
      if (!isQuotaError(e)) throw e;
      if (r === repo) await swapRepo(memoryRepo(), memoryStatus('quota-exceeded'));
    }
  }
  /** Replaces the backing repo and copies the in-memory truth (state, outbox, recents) into it. */
  async function swapRepo(next: Repo, st: StorageStatus) {
    const old = repo;
    repo = next;
    status = st;
    void writes.then(() => old.close());
    emit();
    if (serverState) await next.kvSet('userState', serverState);
    for (const e of outbox) await next.outboxPut(e);
    await next.kvSet('recentSearches', recents);
  }
  function onClosed(r: Repo) {
    if (r === repo) void swapRepo(memoryRepo(), memoryStatus('closed-by-another-tab'));
  }

  // ---- scope (STORE01/STORE02) ----
  function setScope(next: StoreScope | null): Promise<void> {
    const nextKey = next ? dbName(next) : null;
    if (next && loggedOutAt && (next.sessionUpdatedAt ?? 0) <= loggedOutAt) return ready; // session data from before sign-out must not recreate a database
    if (nextKey === key) {
      if (next && scope) {
        const wasShared = scope.sharedMode;
        scope = next;
        if (wasShared !== next.sharedMode) syncShared();
        if (unauthorized) { // the session was re-established
          unauthorized = false;
          void refresh().then(() => flush({ force: true }));
        }
      }
      return ready;
    }
    const old = scope;
    const oldKey = key;
    gen++;
    clearTimeout(timer);
    inFlight.clear();
    const oldRepo = repo;
    void writes.then(() => oldRepo.close()); // let queued writes of the old scope land first
    if (old?.connectionId && oldKey && next && !next.connectionIds.includes(old.connectionId)) void deleteDb(oldKey);
    scope = next;
    key = nextKey;
    if (old) { // pre-session mutations (old === null) stay queued for the first scope
      serverState = null;
      outbox = [];
      recents = [];
      persistAsked = false;
      clearLocalIndex();
    }
    error = null;
    stale = false;
    unauthorized = false;
    failures = 0;
    retryAt = 0;
    loading = true;
    repo = memoryRepo();
    status = IDLE;
    emit();
    ready = next && nextKey ? open(gen, next, nextKey) : Promise.resolve();
    return ready;
  }

  async function open(g: number, s: StoreScope, name: string) {
    let r: Repo;
    let st: StorageStatus;
    if (s.sharedMode) {
      await deleteDb(name);
      r = memoryRepo();
      st = memoryStatus(SHARED);
    } else {
      try {
        r = await openIdbRepo(name, () => onClosed(r));
        known.add(name);
        st = { mode: 'indexeddb', persisted: await isPersisted(), localSavingUnavailable: false, reason: null };
      } catch (e) {
        r = memoryRepo();
        st = memoryStatus(openFailure(e));
      }
    }
    budget = await storageBudget();
    const [saved, pending, savedRecents, asked] = await Promise.all([
      r.kvGet<UserState>('userState'), r.outboxAll(), r.kvGet<string[]>('recentSearches'), r.kvGet<boolean>('persistRequested'),
    ]).catch(() => [undefined, [] as OutboxEntry[], undefined, undefined] as const);
    if (g !== gen) { r.close(); return; }
    repo = r;
    status = st;
    if (saved && !serverState) { serverState = saved; stale = true; loading = false; }
    const ids = new Set(pending.map((e) => e.operationId));
    const early = outbox.filter((e) => !ids.has(e.operationId));
    outbox = [...pending, ...early].sort((a, b) => a.createdAt - b.createdAt);
    lastTs = Math.max(lastTs, ...outbox.map((e) => e.createdAt));
    recents = savedRecents ?? recents;
    persistAsked = !!asked;
    for (const e of early) void write((x) => x.outboxPut(e));
    void write(async (x) => { if ((await x.cacheBytes()) > budget) await x.evict(budget, Date.now()); });
    emit();
    void refresh();
    void flush();
  }

  /** Shared device mode (session or preference) keeps everything in memory and deletes the scope's database. */
  function syncShared() {
    sharedSync = sharedSync.then(async () => {
      await ready;
      const g = gen;
      const name = key;
      if (!scope || !name) return;
      const want = scope.sharedMode || !!snap.state?.preferences.sharedDeviceMode;
      if (want === (status.reason === SHARED)) return;
      if (want) {
        await swapRepo(memoryRepo(), memoryStatus(SHARED));
        await deleteDb(name);
        return;
      }
      try {
        const r: Repo = await openIdbRepo(name, () => onClosed(r));
        if (g !== gen) { r.close(); return; }
        known.add(name);
        await swapRepo(r, { mode: 'indexeddb', persisted: await isPersisted(), localSavingUnavailable: false, reason: null });
      } catch (e) {
        if (g === gen) await swapRepo(memoryRepo(), memoryStatus(openFailure(e)));
      }
    });
  }

  // ---- server state ----
  function adopt(s: UserState): boolean {
    if (serverState && s.revision < serverState.revision) return false;
    serverState = s;
    void write((r) => r.kvSet('userState', s));
    queueMicrotask(syncShared);
    return true;
  }

  async function refresh(): Promise<void> {
    const g = gen;
    if (!scope) return;
    await ready;
    if (g !== gen) return;
    try {
      const s = await api<UserState>('GET', '/api/profile/state');
      if (g !== gen) return;
      adopt(s);
      error = null;
      stale = false;
    } catch (e) {
      if (g !== gen) return;
      error = toApiError(e);
      if (error.status === 401) unauthorized = true;
      stale = serverState !== null;
    }
    loading = false;
    emit();
  }

  // ---- outbox (STORE03) ----
  function mutate(op: StateOperation, opts: { delayMs?: number } = {}): MutationHandle {
    const now = Date.now();
    lastTs = Math.max(now, lastTs + 1);
    const entry: OutboxEntry = {
      operationId: crypto.randomUUID(), baseRevision: serverState?.revision ?? 0, operation: op,
      createdAt: lastTs, notBefore: now + Math.max(0, opts.delayMs ?? 0), attempts: 0,
    };
    if (op.op === 'progress.update') { // newest position wins; an in-flight batch is left alone
      const replaced = outbox
        .filter((e) => !inFlight.has(e.operationId) && e.operation.op === 'progress.update' && e.operation.itemId === op.itemId && e.operation.sessionId === op.sessionId)
        .map((e) => e.operationId);
      if (replaced.length) {
        outbox = outbox.filter((e) => !replaced.includes(e.operationId));
        void write((r) => r.outboxDelete(replaced));
      }
    }
    outbox = [...outbox, entry];
    void write((r) => r.outboxPut(entry));
    emit();
    if (op.op === 'preferences.set' && op.preferences.sharedDeviceMode !== undefined) syncShared();
    if (entry.notBefore > now) schedule();
    else queueMicrotask(() => void flush());
    return { operationId: entry.operationId, undo: () => undo(entry.operationId) };
  }

  /** Removes a not-yet-sent entry (e.g. the undo toast of a delayed op). False once it is in flight or settled. */
  function undo(id: string): boolean {
    if (inFlight.has(id) || !outbox.some((e) => e.operationId === id)) return false;
    outbox = outbox.filter((e) => e.operationId !== id);
    void write((r) => r.outboxDelete([id]));
    emit();
    schedule();
    return true;
  }

  /** Resolves after a flush pass that started after this call. Concurrent calls share one queued pass. */
  function flush(o: FlushOptions = {}): Promise<void> {
    if (queued) {
      queued.force ||= !!o.force;
      queued.keepalive ||= !!o.keepalive;
      return queued.p;
    }
    const q = { p: Promise.resolve(), force: !!o.force, keepalive: !!o.keepalive };
    q.p = running.then(() => {
      queued = null;
      return pass(q.force, q.keepalive);
    });
    queued = q;
    running = q.p.catch((e: unknown) => console.warn('[store] flush failed', e));
    return q.p;
  }

  async function pass(force: boolean, keepalive: boolean) {
    const g = gen;
    if (!scope || unauthorized || (!force && Date.now() < retryAt)) return;
    await ready;
    await writes;
    if (g !== gen) return;
    const locks = globalThis.navigator?.locks;
    if (locks) await locks.request('tuned-outbox', () => sendDue(g, keepalive));
    else await sendDue(g, keepalive);
    if (g === gen) schedule();
  }

  async function sendDue(g: number, keepalive: boolean) {
    for (;;) {
      if (g !== gen || unauthorized) return;
      const now = Date.now();
      const batch = outbox.filter((e) => e.notBefore <= now && !inFlight.has(e.operationId)).slice(0, BATCH);
      if (!batch.length || !(await send(batch, g, keepalive))) return;
    }
  }

  /** Sends one batch. False = stop (offline/backoff or unauthorized). Retries always reuse the same operationIds. */
  async function send(batch: OutboxEntry[], g: number, keepalive: boolean): Promise<boolean> {
    for (const e of batch) inFlight.add(e.operationId);
    try {
      const res = await api<StatePatchResponse>('PATCH', '/api/profile/state', {
        operations: batch.map((e) => ({ operationId: e.operationId, baseRevision: e.baseRevision, operation: e.operation })),
      }, { keepalive });
      if (g !== gen) return false;
      failures = 0;
      retryAt = 0;
      const unsettled = settle(res, batch);
      if (!unsettled.length) return true;
      backoff(unsettled); // a result missing from the response: retry later instead of spinning
      return false;
    } catch (err) {
      if (g !== gen) return false;
      const e = toApiError(err);
      if (e.status === 401 || e.code === 'UNAUTHORIZED') {
        unauthorized = true;
        error = e;
        emit();
        return false;
      }
      if (e.code === 'VALIDATION_FAILED') { // no poison queue: isolate and drop only the invalid ops
        if (batch.length === 1) {
          drop([batch[0].operationId]);
          emit();
          return true;
        }
        for (const one of batch) {
          inFlight.delete(one.operationId);
          if (!(await send([one], g, keepalive))) return false;
        }
        return true;
      }
      backoff(batch);
      return false;
    } finally {
      for (const e of batch) inFlight.delete(e.operationId);
    }
  }

  function backoff(entries: OutboxEntry[]) {
    failures++;
    retryAt = Date.now() + backoffDelay(failures);
    for (const x of entries) {
      x.attempts++;
      void write((r) => r.outboxPut(x));
    }
  }

  function drop(ids: string[]) {
    const gone = new Set(ids);
    outbox = outbox.filter((e) => !gone.has(e.operationId));
    void write((r) => r.outboxDelete(ids));
  }

  /** Every returned result (applied, duplicate, stale, rejected) settles its entry; the response state is authoritative. Returns unsettled entries. */
  function settle(res: StatePatchResponse, batch: OutboxEntry[]): OutboxEntry[] {
    const done = res.results.map((r) => r.operationId);
    drop(done);
    adopt(res.state);
    stale = false;
    if (error?.status !== 401) error = null;
    emit();
    post('state', { key, state: res.state, done });
    const ops = new Map(batch.map((e) => [e.operationId, e.operation.op]));
    if (res.results.some((r) => r.status === 'applied' && MEANINGFUL.has(ops.get(r.operationId)!))) void requestPersist();
    return batch.filter((e) => !done.includes(e.operationId));
  }

  function schedule() {
    clearTimeout(timer);
    timer = undefined;
    if (!scope || unauthorized || !outbox.length) return;
    const due = Math.max(retryAt, Math.min(...outbox.map((e) => e.notBefore)));
    timer = setTimeout(() => void flush({ force: true }), Math.max(0, due - Date.now()));
  }

  /** STORE04: ask once, only after the first meaningful save, and accept denial. */
  async function requestPersist() {
    if (persistAsked || repo.mode !== 'indexeddb') return;
    persistAsked = true;
    void write((r) => r.kvSet('persistRequested', true));
    let persisted = false;
    try { persisted = (await globalThis.navigator?.storage?.persist?.()) ?? false; } catch { /* denied */ }
    status = { ...status, persisted };
    emit();
  }

  // ---- cache (STORE02) ----
  async function cacheGet<T>(k: string): Promise<CacheRecord<T> | undefined> {
    if (!cacheable(k)) return undefined;
    await ready;
    let rec: CacheRecord | undefined;
    try { rec = await repo.cacheGet(k); } catch { return undefined; }
    if (rec && Date.now() - rec.lastUsedAt > TOUCH_MS) {
      void write(async (r) => {
        const cur = await r.cacheGet(k);
        if (cur) await r.cachePut({ ...cur, lastUsedAt: Date.now() });
      });
    }
    return rec as CacheRecord<T> | undefined;
  }

  async function cachePut<T>(k: string, data: T, o: CachePutOptions): Promise<void> {
    if (!cacheable(k)) return;
    await ready;
    const now = Date.now();
    const bytes = approxBytes(data);
    if (bytes > budget) return;
    const rec: CacheRecord = {
      key: k, kind: o.kind, data, fetchedAt: now, staleAt: now + o.maxAgeMs, sourceRevision: o.sourceRevision ?? null,
      schemaVersion: SCHEMA_VERSION, bytes, lastUsedAt: now,
    };
    await write(async (r) => {
      if ((await r.cacheBytes()) + bytes > budget) await r.evict(budget - bytes, now);
      await r.cachePut(rec);
    });
  }

  async function cacheDelete(k: string): Promise<void> {
    await ready;
    await write((r) => r.cacheDelete(k));
    post('invalidate', [k]);
    invalidateCbs.forEach((cb) => cb([k]));
  }

  /** AUTH05 "Clear downloaded metadata": drops catalog/guide/detail/search/artwork cache records and the local search
   * index; user state, the outbox and recent searches stay. Other tabs are told to refetch what they show. */
  async function clearCachedMetadata(): Promise<void> {
    await ready;
    let keys: string[] = [];
    await write(async (r) => { keys = await r.cacheClear(); });
    clearLocalIndex();
    post('invalidate', keys);
    invalidateCbs.forEach((cb) => cb(keys));
  }

  // ---- recent searches (kv in the scoped repo; memory in shared mode; never localStorage) ----
  function addRecent(q: string) {
    const t = q.trim().slice(0, 200);
    if (!t) return;
    recents = [t, ...recents.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, MAX_RECENTS);
    void write((r) => r.kvSet('recentSearches', recents));
    emit();
  }
  function clearRecents() {
    recents = [];
    void write((r) => r.kvSet('recentSearches', recents));
    emit();
  }

  // ---- sign-out (AUTH05) ----
  function resetLocal() {
    loggedOutAt = Date.now();
    gen++;
    clearTimeout(timer);
    timer = undefined;
    inFlight.clear();
    repo.close();
    repo = memoryRepo();
    status = IDLE;
    scope = null;
    key = null;
    ready = Promise.resolve();
    serverState = null;
    outbox = [];
    recents = [];
    error = null;
    stale = false;
    unauthorized = false;
    loading = true;
    failures = 0;
    retryAt = 0;
    persistAsked = false;
    clearLocalIndex();
    emit();
  }

  /** Deletes every Tuned database and Cache Storage entry on this origin and tells other tabs. Never touches server state. */
  async function signOut(): Promise<void> {
    const names = new Set(known);
    if (key) names.add(key);
    post('logout'); // other tabs close their connections so the deletes below are not blocked
    resetLocal();
    for (const n of await tunedDbNames()) names.add(n);
    await Promise.all([...names].filter((n) => n.startsWith('tuned:')).map(deleteDb));
    try {
      if (typeof caches !== 'undefined') await Promise.all((await caches.keys()).map((k) => caches.delete(k)));
    } catch { /* Cache Storage unavailable */ }
    logoutCbs.forEach((cb) => cb());
  }

  function dispose() {
    gen++;
    clearTimeout(timer);
    const r = repo;
    void writes.then(() => r.close());
    channel?.close();
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVisibility);
    }
  }

  const addTo = <T>(set: Set<T>, v: T) => { set.add(v); return () => { set.delete(v); }; };

  return {
    subscribe: (l: () => void) => addTo(listeners, l),
    getSnapshot: () => snap,
    setScope, refresh, mutate, flush, cacheGet, cachePut, cacheDelete, clearCachedMetadata, addRecent, clearRecents, signOut, dispose,
    broadcast: post,
    subscribeBroadcast(type: string, cb: (payload: unknown) => void) {
      if (!bus.has(type)) bus.set(type, new Set());
      return addTo(bus.get(type)!, cb);
    },
    onLogout: (cb: () => void) => addTo(logoutCbs, cb),
    onInvalidate: (cb: (keys: string[]) => void) => addTo(invalidateCbs, cb),
  };
}

let singleton: Store | undefined;
/** The tab's store. Created lazily so importing the module has no side effects. */
export const getStore = (): Store => (singleton ??= createStore());
